<template>
  <GlassModal
    :trigger="''"
    :title="'Share playlist'"
    :subtitle="modalStore?.sharePlaylistModal?.playlistModal?.title"
    :is-open="modalStore.sharePlaylistModal.state"
    @did-dismiss="handleDismiss"
  >
    <div class="loading-overlay" v-if="waitingForSource">
      <ion-spinner name="crescent" class="loading-spinner"></ion-spinner>
    </div>
    <!-- permissions component -->
    <div class="permissions-wrapper">
      <!-- access toggle wrapper -->
      <div class="toggle-wrapper">
        <span class="permissions-title-text">🔒 Anyone with this link can:</span
        ><!-- access toggle button --><button
          class="permissions-button"
          @click="updatePermissions('toggleAccess')"
        >
          <div class="button-icon-wrapper">
            <ArrowDownCircleIcon
              v-if="
                !showLinkAccessSpinner &&
                shareLinkAccessDisplay === 'Download' &&
                !showToggleAccessSuccess
              "
              class="button-icon"
            ></ArrowDownCircleIcon>
            <MusicalNoteIcon
              v-if="
                !showLinkAccessSpinner &&
                shareLinkAccessDisplay === 'Listen' &&
                !showToggleAccessSuccess
              "
              class="button-icon"
            ></MusicalNoteIcon>
            <ion-spinner
              v-if="showLinkAccessSpinner && !showToggleAccessSuccess"
              class="button-icon"
              name="crescent"
            ></ion-spinner>
            <CheckCircleIcon
              v-if="showToggleAccessSuccess"
              class="button-icon"
              style="color: var(--ion-color-success)"
            ></CheckCircleIcon>
          </div>
          <span
            class="button-label-text"
            :style="{
              color: showToggleAccessSuccess
                ? 'var(--ion-color-success)'
                : 'var(--ion-color-step-800)',
            }"
            >{{ shareLinkAccessDisplay }}</span
          >
        </button>
      </div>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="65"
        height="2"
        viewBox="0 0 65 2"
        fill="none"
      >
        <path d="M0.5 1H64.5" stroke="#52525B" stroke-linecap="round" />
      </svg>
      <!-- reset link button -->
      <button class="reset-link-button" @click="handleResetLinkIntend">
        <div>
          <ion-spinner
            v-if="
              mutation.state === 'loading' &&
              mutation.name === 'resetLink' &&
              !showResetLinkSuccess
            "
            name="crescent"
            class="button-icon"
            style="color: var(--ion-color-danger)"
          ></ion-spinner>
          <CheckCircleIcon
            v-if="showResetLinkSuccess"
            class="button-icon"
            style="color: var(--ion-color-success)"
          ></CheckCircleIcon>
        </div>
        <span
          class="reset-link-text"
          :style="{
            color: showResetLinkSuccess
              ? 'var(--ion-color-success)'
              : 'var(--ion-color-danger)',
          }"
          >{{ resetLinkButtonLabel }}</span
        >
      </button>
    </div>
    <!-- share button -->
    <button class="share-button" @click="handlePrimaryButtonClick">
      <div class="button-icon-wrapper">
        <CheckIcon
          v-if="primaryButtonState.icon === 'success-icon'"
          class="button-icon"
        ></CheckIcon>
        <ShareIcon
          v-if="primaryButtonState.icon === 'native-share-icon'"
          class="button-icon"
        ></ShareIcon>
        <DocumentDuplicateIcon
          v-if="primaryButtonState.icon === 'copy-link-icon'"
          class="button-icon"
        ></DocumentDuplicateIcon>
      </div>
      {{ primaryButtonState.label }}
    </button></GlassModal
  >
</template>

<script setup>
import GlassModal from '@/components/GlassModal.vue';
import { useModalStore, ModalState } from '@/stores/useModalStore';
import {
  IonSpinner,
  alertController,
  isPlatform,
  toastController,
} from '@ionic/vue';
import useCopyClipboard from '@/composables/useCopyClipboard';
import {
  ArrowDownCircleIcon,
  ShareIcon,
  MusicalNoteIcon,
  CheckIcon,
  CheckCircleIcon,
  DocumentDuplicateIcon,
} from '@heroicons/vue/24/outline';
import { computed, ref, watchEffect, defineProps } from 'vue';
import useUpdatePlaylistPermissions from '@/composables/useUpdatePlaylistPermissions';
import { Share } from '@capacitor/share';

const modalStore = useModalStore();
const { updatePlaylistPermissions, mutation } = useUpdatePlaylistPermissions();
const showResetLinkSuccess = ref(false);
const showToggleAccessSuccess = ref(false);
const { copyClipboard } = useCopyClipboard();
const linkCopied = ref(false);

// props

const props = defineProps({
  context: String,
});

// set context

watchEffect(() => {
  if (props.context && !modalStore.sharePlaylistModal.context) {
    modalStore.sharePlaylistModal.context = props.context;
  }
});

// waiting for src?

const waitingForSource = computed(() => {
  const { sharePlaylistModal } = modalStore;
  const { playlistModal } = sharePlaylistModal || {};

  const isPlaylistModalAbsent = !playlistModal?.id;

  return isPlaylistModalAbsent;
});

// lower case shareLinkAccess

const shareLinkAccessDisplay = computed(() => {
  const access = modalStore?.sharePlaylistModal?.playlistModal?.shareLinkAccess;
  switch (access) {
    case 'LISTEN':
      return 'Listen';
    case 'DOWNLOAD':
      return 'Download';
    default:
      return 'Listen';
  }
});

// show linkAccess spinner?

const showLinkAccessSpinner = computed(() => {
  if (
    mutation.value.state === 'loading' &&
    mutation.value.name === 'toggleAccess'
  ) {
    return true;
  }
  return false;
});

// show linkAccess toggle success?

watchEffect(() => {
  if (
    mutation.value.state === 'idle' &&
    mutation.value.name === 'toggleAccess'
  ) {
    showToggleAccessSuccess.value = true;
    setTimeout(() => {
      showToggleAccessSuccess.value = false;
    }, 2000);
  }
});

// show reset link success?

watchEffect(() => {
  if (mutation.value.state === 'idle' && mutation.value.name === 'resetLink') {
    showResetLinkSuccess.value = true;
    setTimeout(() => {
      showResetLinkSuccess.value = false;
    }, 2000);
  }
});

// dismiss modal

function handleDismiss() {
  modalStore.sharePlaylistModal.state = ModalState.CLOSED;
}

// mutate playlist permissions

async function updatePermissions(type) {
  const playlistId = modalStore?.sharePlaylistModal?.playlistModal?.id;

  let newAccess;

  switch (modalStore?.sharePlaylistModal?.playlistModal?.shareLinkAccess) {
    case 'LISTEN':
      newAccess = 'DOWNLOAD';
      break;
    case 'DOWNLOAD':
      newAccess = 'LISTEN';
      break;
  }

  if (type === 'toggleAccess') {
    modalStore.updatePlaylistModalData('sharePlaylistModal', {
      shareLinkAccess: newAccess,
    });
  }

  await updatePlaylistPermissions({
    playlistId: playlistId,
    access: newAccess,
    resetShareLink: type === 'resetLink' ? true : false,
  });
}

// open confirm reset

const handleResetLinkIntend = async () => {
  const confirmResetLinkAlert = await alertController.create({
    header: 'Are you sure?',
    message: 'This will reset access to this link.',
    buttons: alertButtonsResetConfirm,
    backdropDismiss: false,
  });
  await confirmResetLinkAlert.present();
};

// alert content - confirm reset link

const alertButtonsResetConfirm = [
  {
    text: 'Cancel',
    role: 'cancel',
    handler: () => {
      return;
    },
  },
  {
    text: "I'm sure",
    role: 'confirm',
    handler: () => {
      updatePermissions('resetLink');
    },
  },
];

// isIos?

const isIos = computed(() => {
  return isPlatform('ios') && isPlatform('hybrid');
});

// native share

async function nativeShare(shareUrl, shareText) {
  try {
    await Share.share({
      text: shareUrl,
      url: shareText,
    });
  } catch (error) {
    linkCopied.value = 'error';
    setTimeout(() => {
      linkCopied.value = 'false';
    }, 1500);
  }
}

// create new share link

const originalShareUrl = computed(
  () => modalStore.sharePlaylistModal.playlistModal.shareLink,
);

const shareLinkId = computed(() => {
  const urlParts = originalShareUrl.value.split('/');
  return urlParts.pop();
});

const shareUrl = computed(() =>
  shareLinkId.value
    ? `https://share.crate.fm/share/${shareLinkId.value}`
    : null,
);

// handle primary button click

function handlePrimaryButtonClick() {
  if (isIos.value) {
    nativeShare(shareUrl.value);
  } else {
    linkCopied.value = true;
    copyClipboard({
      copyValue: shareUrl.value,
      successMessage: 'Link copied to clipboard',
      successColor: 'success',
      disableErrorMessage: true,
    });
    setTimeout(() => {
      linkCopied.value = false;
    }, 1500);
  }
}

// primary button state

const primaryButtonState = computed(() => {
  let icon = '';
  let label = '';

  if (linkCopied.value && !isIos.value) {
    icon = 'success-icon';
    label = 'Link copied';
  } else {
    label = isIos.value ? 'Share' : 'Copy link';
    icon = isIos.value ? 'native-share-icon' : 'copy-link-icon';
  }
  return { icon, label };
});

// does playlist have songs?

const songs = computed(
  () => modalStore.sharePlaylistModal?.playlistModal?.songs,
);

const doesPlaylistHaveSongs = computed(() => {
  if (songs.value) {
    return songs.value.length > 0;
  } else {
    return true;
  }
});

watchEffect(() => {
  if (
    !waitingForSource.value &&
    !doesPlaylistHaveSongs.value &&
    modalStore.isModalOpen('sharePlaylistModal')
  ) {
    // do nothing for now, can add function handleNoSongsInPlaylist (below) in the future if needed
  }
});

// handle no songs in playlist

// const handleNoSongsInPlaylist = async () => {
//   modalStore.sharePlaylistModal.state = ModalState.CLOSED;
//   const toast = await toastController.create({
//     header: 'Add songs to share this playlist',
//     duration: 1500,
//     color: 'light',
//   });
//   await toast.present();
//   return;
// };

// reset link button label

const resetLinkButtonLabel = computed(() => {
  if (
    mutation.value.state === 'loading' &&
    mutation.value.name === 'resetLink'
  ) {
    return 'Resetting link';
  } else return showResetLinkSuccess.value ? 'Link reset' : 'Reset this link';
});
</script>

<style>
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
}

.loading-spinner {
  color: var(--ion-color-step-800);
  position: relative;
}

.permissions-wrapper {
  display: flex;
  padding: 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  align-self: stretch;
  border-radius: 32px;
  border: 1px solid var(--ion-border-primary);
  width: 80vw;
  max-width: 21rem;
  box-shadow: 0px 8px 40px 0px rgba(0, 0, 0, 0.24);
  background: rgba(82, 82, 91, 0.5);
}

.toggle-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
}

.permissions-title-text {
  color: var(--ion-color-step-800);
  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
}

.permissions-button {
  display: flex;
  padding: 8px 32px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 100px;
  border: 1px solid var(--ion-border-primary);
  background: var(--ion-color-step-400);
  box-shadow: 0px 8px 40px 0px rgba(0, 0, 0, 0.24);
  transition: all 0.3s ease;
  transform: scale(1);
  width: 100%;
}

.permissions-button:hover {
  background: var(--ion-color-step-400);
  transform: scale(1.03);
}

.permissions-button:active {
  background: var(--ion-color-step-400);
  transform: scale(0.98);
}

.button-icon {
  height: 24px;
  width: 24px;
  color: var(--ion-color-step-800);
}

.button-label-text {
  color: var(--ion-color-step-800);
  text-align: center;
  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  margin: 0px;
  padding: 0px;
}

.reset-link-button {
  background-color: transparent;
  margin: 0px;
  padding: 0px;
  transition: all 0.3s ease;
  transform: scale(1);
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 16px;
}

.reset-link-button:hover {
  transform: scale(1.03);
}

.reset-link-button:active {
  transform: scale(0.98);
}

.reset-link-text {
  color: var(--ion-color-danger);
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
}

.share-button {
  border-radius: 100px;
  border: 1px solid var(--ion-border-primary);
  background: var(--ion-color-gradient-primary);
  box-shadow: 0px 8px 40px 0px rgba(0, 0, 0, 0.24);
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  color: var(--ion-color-step-800);
  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  height: 48px;
  transform: scale(1);
  transition: all 0.3s ease;
}

.share-button:hover {
  transform: scale(1.03);
}

.share-button:active {
  transform: scale(0.98);
}
</style>
